"use client";

import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent } from "@/components/ui/card";
import { Check, X, Shield, Zap, Award, Ruler } from "lucide-react";
export default function Advantage() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.4
  });
  const comparison = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const benefits = [{
    title: "Precision Engineering",
    description: "Every panel is laser-measured and custom-fabricated for a zero-tolerance fit.",
    icon: <Ruler className="size-5" />
  }, {
    title: "Climate Resilience",
    description: "Materials tested against Alberta's extreme freeze-thaw cycles and high-velocity winds.",
    icon: <Shield className="size-5" />
  }, {
    title: "Rapid Installation",
    description: "Proprietary mounting systems reduce site time by 30% without compromising integrity.",
    icon: <Zap className="size-5" />
  }, {
    title: "Lifetime Assurance",
    description: "Lifetime limited material warranty backed by the manufacturer.",
    icon: <Award className="size-5" />
  }];
  const comparisonData = [{
    feature: "Material Grade",
    standard: "Builder Grade",
    premium: "Architectural Grade"
  }, {
    feature: "Fastening System",
    standard: "Exposed Nails",
    premium: "Hidden Clip System"
  }, {
    feature: "Weather Barrier",
    standard: "Standard Wrap",
    premium: "High-Perm Shield"
  }, {
    feature: "Warranty",
    standard: "5-10 Years",
    premium: "Lifetime limited material warranty backed by the manufacturer"
  }, {
    feature: "Customization",
    standard: "Limited Colors",
    premium: "Bespoke Fabrication"
  }];
  return <section data-section-id="11903" id="the-hardie-lp-advantage" className="bg-background text-foreground overflow-hidden ![padding:30px_0px_30px_0px]">
      <div className="container mx-auto px-6 md:px-14 max-w-[1440px]">
        {}
        <div ref={header.ref} className="max-w-4xl mb-20 md:mb-32">
          <div className="flex items-center gap-4 mb-6">
            
            <div className="h-px flex-grow bg-border"></div>
          </div>
          
          <h2 className="font-default font-semibold text-foreground leading-[1.1] mb-8" style={{
          fontSize: "clamp(2.5rem, 5vw, 4rem)",
          letterSpacing: "-0.03em",
          opacity: header.isVisible ? 1 : 0,
          transform: header.isVisible ? "translateY(0)" : "translateY(20px)",
          transition: "opacity 600ms cubic-bezier(0.16, 1, 0.3, 1), transform 600ms cubic-bezier(0.16, 1, 0.3, 1)"
        }}>The <span className="italic font-serif text-primary" data-ast-id="f:/src/pages/SidingCladding/sections/Advantage.tsx|sig:qnz7t8" data-ast-start="2642" data-ast-end="2711">Architectural</span>&nbsp; Edge.</h2>

          <p className="text-foreground/70 max-w-2xl font-default" style={{
          fontSize: "clamp(1.125rem, 1.5vw, 1.375rem)",
          lineHeight: 1.6,
          opacity: header.isVisible ? 1 : 0,
          transform: header.isVisible ? "translateY(0)" : "translateY(20px)",
          transition: "opacity 600ms cubic-bezier(0.16, 1, 0.3, 1) 100ms, transform 600ms cubic-bezier(0.16, 1, 0.3, 1) 100ms"
        }}>
            We don't just install siding; we engineer exterior envelopes. Our methodology combines high-performance materials with precision craftsmanship to deliver Calgary's most resilient homes.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-20">
          {}
          <div className="lg:col-span-5 grid grid-cols-1 gap-10">
            {benefits.map((benefit, i) => <div key={i} data-index={i} className="group flex gap-6">
                <div className="flex-shrink-0 size-12 rounded-lg bg-card border border-border flex items-center justify-center text-primary transition-colors duration-300 group-hover:border-primary">
                  {benefit.icon}
                </div>
                <div>
                  <h3 className="font-default font-semibold text-xl mb-2">{benefit.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
                </div>
              </div>)}
          </div>

          {}
          <div ref={comparison.ref} className="lg:col-span-7" style={{
          opacity: comparison.isVisible ? 1 : 0,
          transform: comparison.isVisible ? "translateY(0)" : "translateY(20px)",
          transition: "opacity 800ms cubic-bezier(0.16, 1, 0.3, 1) 200ms, transform 800ms cubic-bezier(0.16, 1, 0.3, 1) 200ms"
        }}>
            <Card className="bg-secondary text-secondary-foreground border-none rounded-lg overflow-hidden shadow-xl">
              <CardContent className="p-0">
                <div className="p-8 md:p-12">
                  <div className="flex justify-between items-end mb-10">
                    <h3 className="font-default font-semibold text-2xl md:text-3xl">Performance Comparison</h3>
                    
                  </div>

                  <div className="space-y-0">
                    <div className="grid grid-cols-3 pb-4 border-b border-white/10 font-mono text-[10px] uppercase tracking-widest opacity-50">
                      <div>Feature</div>
                      <div className="text-center">Standard</div>
                      <div className="text-right text-primary">MetalLine</div>
                    </div>
                    
                    {comparisonData.map((row, i) => <div key={i} data-index={i} className="grid grid-cols-3 py-5 border-b border-white/5 items-center group hover:bg-white/5 transition-colors px-2 -mx-2">
                        <div className="font-default font-medium text-sm md:text-base">{row.feature}</div>
                        <div className="text-center text-xs md:text-sm text-white/40 flex items-center justify-center gap-2">
                          <X className="size-3 text-destructive/50" />
                          {row.standard}
                        </div>
                        <div className="text-right text-xs md:text-sm font-semibold text-primary flex items-center justify-end gap-2">
                          
                          {row.premium}
                        </div>
                      </div>)}
                  </div>

                  <div className="mt-12 flex flex-col sm:flex-row items-center gap-8">
                    <Link to="/ContactPage" className="w-full sm:w-auto bg-primary text-white px-8 py-4 font-mono text-xs uppercase tracking-widest font-bold hover:bg-primary/90 transition-all text-center rounded-lg">REQUEST QUOTE&nbsp;&nbsp;</Link>
                    
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {}
            <div className="mt-6 flex justify-end">
              
            </div>
          </div>
        </div>
      </div>
    </section>;
}